import { quoteCreationSteps } from "@/common/const/quote";
import { QuoteCreationStepStateType } from "@/common/types/quote";
import { Steps } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useNavigate } from "react-router";
import QuoteModalStepsContent from "./QuoteModalStepsContent";
import QuoteModalButtons from "./QuoteModalButtons";
import { z } from "zod";
import { invalidCountryRows, validateByZod } from "@/common/utils";
import { usePermissions } from "@/common/contexts/Permissions.context";
import { useCreateMultiAutomated } from "@/common/api/mutation";
import { useModal } from "@/common/contexts/Modal.context";
import { InfoModal } from "@/components/Modals";
import StayInPageButton from "@/components/ActionsButtons/StayInPageButton";
import GoToPageButton from "@/components/ActionsButtons/GoToPageButton";
import { GlobeLoaderIcon } from "@/components/icon";
import CustomTypography from "@/components/Typography/CustomTypography";
import { QUOTES_TYPE } from "@/common/const/types/index.const";

const initialState: QuoteCreationStepStateType = {
  firstStepData: {
    dueDate: dayjs().add(5, "days").toISOString(),
    productType: "DIA",
    quoteCreatingType: "singleSiteQuote",
    RFSdate: dayjs().add(7, "days").toISOString(),
  },
  secondStepData: {
    customer: null,
    customerProjectNumber: "",
    endCustomerName: "",
    file: null,
  },
};
const SingleSiteQuoteSchema = z.object({
  dueDate: z
    .string({ message: "This field is required" })
    .min(1, "Pick Due Date"),
  RFSdate: z
    .string({ message: "This field is required" })
    .min(1, "Pick RFS Date"),
  quoteCreatingType: z.literal("singleSiteQuote"),
  productType: z.enum(QUOTES_TYPE),
});

const MultiSiteQuoteSchema = z.object({
  dueDate: z
    .string({ message: "This field is required" })
    .min(1, "Pick Due Date"),
  RFSdate: z
    .string({ message: "This field is required" })
    .min(1, "Pick RFS Date"),
  quoteCreatingType: z.literal("multiSiteQuote"),
  productType: z.enum([...QUOTES_TYPE].slice(0, 8) as [string, ...string[]], {
    message: "Incorrect value for multi site quote request",
  }),
});
const secondStepSchema = z.object({
  endCustomerName: z.string().min(1, "This field is required"),
  customerProjectNumber: z.string().min(1, "This field is required"),
  file: z.instanceof(File, { message: "A file is required" }),
});
const stateKeys = ["firstStepData", "secondStepData"] as const;
type StateKeys = (typeof stateKeys)[number];

export default function QuoteModalWrapper() {
  const { openModal } = useModal();
  const { company } = usePermissions();
  const { mutate, isPending } = useCreateMultiAutomated();
  const navigate = useNavigate();
  const [quoteGlobalState, setQuoteGlobalState] =
    useState<QuoteCreationStepStateType>(initialState);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [validationErr, setValidationErr] = useState<Record<string, string>>(
    {}
  );
  const [isResults, setIsResults] = useState<boolean>(false);
  const updateIsResults = (value: boolean) => {
    setIsResults(value);
  };
  const updateQuoteState = (
    parentName: StateKeys,
    fieldName: string,
    value: any
  ) => {
    setQuoteGlobalState((state) => ({
      ...state,
      [parentName]: { ...state[parentName], [fieldName]: value },
    }));
  };
  const removeErrors = (filedName: string) => {
    const newErrors = { ...validationErr };
    delete newErrors[filedName];
    return setValidationErr(newErrors);
  };
  const nextSteps = () => {
    if (
      currentStep === 0 &&
      quoteGlobalState.firstStepData.quoteCreatingType === "singleSiteQuote"
    ) {
      const validatedSingleData = validateByZod(
        SingleSiteQuoteSchema,
        quoteGlobalState.firstStepData
      );
      if (validatedSingleData.withErrors) {
        return setValidationErr(validatedSingleData.data);
      }
      return navigate(
        `/singleSiteQuote/${quoteGlobalState.firstStepData.productType}`,
        {
          state: {
            quoteState: validatedSingleData,
          },
        }
      );
    } else {
      const validatedMultiData = validateByZod(
        MultiSiteQuoteSchema,
        quoteGlobalState.firstStepData
      );
      if (validatedMultiData.withErrors) {
        return setValidationErr(validatedMultiData.data);
      }
      return setCurrentStep((state) => state + 1);
    }
  };
  const prevStep = () => {
    setCurrentStep((state) => state - 1);
  };
  const submitData = () => {
    const secondStepValidationData = validateByZod(
      secondStepSchema,
      quoteGlobalState.secondStepData
    );
    if (secondStepValidationData.withErrors) {
      return setValidationErr(secondStepValidationData.data);
    }
    if (
      company.companyType === "airx" &&
      !quoteGlobalState.secondStepData.customer === null
    ) {
      return setValidationErr((state) => ({
        ...state,
        customer: "Pick customer from list",
      }));
    }
    const reqData = {
      productType: quoteGlobalState.firstStepData.productType,
      endCustomerName: secondStepValidationData.endCustomerName,
      customerProjectNumber: secondStepValidationData.customerProjectNumber,
      companyId: quoteGlobalState.secondStepData.customer?.value || company.id,
      dueDate: quoteGlobalState.firstStepData.dueDate,
      RFSdate: quoteGlobalState.firstStepData.RFSdate,
    };
    const formData = new FormData();
    formData.append("file", quoteGlobalState.secondStepData.file as Blob);
    mutate(
      { data: formData, req: reqData },
      {
        onSuccess: (data: any) => {
          if (data?.resultsData === undefined) {
            return openModal(
              <InfoModal
                actions={[<StayInPageButton />]}
                instruction={{
                  behavior: data.behavior,
                  description: "Validation failed due to errors.",
                  header: "Validation completed",
                }}
                desClassName="max-w-[400px]"
              />
            );
          }
          const { groupId, errors = [] } = data.resultsData;
          const invalidCountries = invalidCountryRows(errors);
          if (groupId) updateIsResults(true);
          return openModal(
            <InfoModal
              actions={[
                <StayInPageButton />,
                groupId ? (
                  <GoToPageButton
                    destinationName="See results"
                    link={`/multiSiteResults/${groupId}`}
                  />
                ) : null,
              ]}
              instruction={{
                behavior: data?.resultsData?.behavior
                  ? data.resultsData.behavior
                  : data.behavior,
                description:
                  (data.resultsData.behavior === "success"
                    ? "All addresses validated!"
                    : "Not all addresses have been successfully validated. You can now create an automatic quote for these addresses. For the addresses that were not validated, please download the CSV file and create a manual quote.") +
                  invalidCountries,
                header: "Validation completed",
              }}
              desClassName="max-w-[400px]"
            />
          );
        },
      }
    );
  };
  return (
    <>
      <CustomTypography
        textAlign="center"
        case="uppercase"
        size={"21px"}
        weight={"semibold"}
        className="mb-4"
      >
        Create new Quote
      </CustomTypography>
      <Steps current={currentStep} items={quoteCreationSteps} />
      <div className=" min-w-[290px] w-[576px] mt-4">
        <QuoteModalStepsContent
          currentStep={currentStep}
          globalState={quoteGlobalState}
          updateFunc={updateQuoteState}
          validationErrors={validationErr}
          removeErrors={removeErrors}
          updateIsResults={updateIsResults}
        />
      </div>
      {!isPending && (
        <QuoteModalButtons
          currentStep={currentStep}
          nextStep={nextSteps}
          prevStep={prevStep}
          isResults={isResults}
          submitData={submitData}
          productType={quoteGlobalState.firstStepData.productType}
        />
      )}
      {isPending && (
        <div className="flex items-center justify-center">
          <GlobeLoaderIcon width="48" height="48" />
        </div>
      )}
    </>
  );
}
